// angular
import { Title } from '@angular/platform-browser';
import { ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
// other libraries
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { BreadcrumbsComponent } from '@app/core/components/breadcrumbs/breadcrumbs.component';
import { OptionsMenuComponent } from '@app/core/components/options-menu/options-menu.component';
import { BarcodeDialogComponent } from '@app/core/dialogs/barcode-dialog/barcode-dialog.component';
import { GalleryComponent } from '@app/core/components/gallery/gallery.component';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { PreviewMenuOptionsType } from '@app/+products/shared/types/product-preview.type';
import { AuctionMetaCardComponent } from '../shared/components/auction-meta-card/auction-meta-card.component';
import { AuctionsDetailSkeletonComponent } from './components/auctions-detail-skeleton/auctions-detail-skeleton.component';
import { AuctionLotInfoComponent } from './components/auction-lot-info/auction-lot-info.component';
import { AuctionBidPanelComponent } from './components/auction-bid-panel/auction-bid-panel.component';
import { AuctionSupportCardComponent } from './components/auction-support-card/auction-support-card.component';
import { AuctionTermsSectionComponent } from './components/auction-terms-section/auction-terms-section.component';
// services
import { AuctionsService } from '../shared/services/auctions.service';
import { StorageService } from '@app/shared/services/storage.service';
// env & enums & types
import { environment } from '@environments/environment';
import { AuctionStatesEnum } from '../shared/enums/auction-states.enum';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { IMedia } from '@app/shared/interfaces/media.interface';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { IAuction } from '../shared/types/auctions.type';
import { Roles } from '@app/shared/enums/roles.enum';
import { IBreadcrumbs } from '@app/shared/interfaces/breadcrumbs.interface';
// pipes & directives
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { UserService } from '@app/shared/services/user.service';

@Component({
  selector: 'pwj-auctions-detail',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    OptionsMenuComponent,
    TranslateModule,
    TranslateDataPipe,
    GalleryComponent,
    DecimalPipe,
    AuctionsDetailSkeletonComponent,
    AuctionMetaCardComponent,
    AuctionLotInfoComponent,
    AuctionBidPanelComponent,
    AuctionSupportCardComponent,
    AuctionTermsSectionComponent,
  ],
  templateUrl: './auctions-detail.component.html',
  styleUrl: './auctions-detail.component.scss',
})
export class AuctionsDetailComponent {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public currentLang!: string;
  public auctionStates = AuctionStatesEnum;
  public auction!: IAuction;
  public isAuctionDetailsLoading: boolean = false;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;
  public breadcrumbsData: IBreadcrumbs[] = [];
  public menuOptions: PreviewMenuOptionsType[] = [];
  public _auctionId!: string;

  // dependencies
  private readonly _storageService = inject(StorageService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _auctionsService = inject(AuctionsService);
  private readonly _dialogService = inject(DialogService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _titleService = inject(Title);
  private readonly _translateService = inject(TranslateService);
  private readonly _translateData = inject(TranslateDataPipe);
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _userService = inject(UserService);

  constructor() {
    this.currentLang = this._translateService.currentLang;

    this._translateService.onLangChange.subscribe(data => {
      this.currentLang = data.lang;
      this.setBreadcrumbs();
    });

    this._activatedRoute.params.subscribe(params => {
      this._auctionId = params?.['auctionId'];
      this._auctionsService.getAuctionDetails(this._auctionId);
    });

    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;

    effect(() => {
      const { product, ...rest } = this._auctionsService.auctionDetails();

      let media: IMedia[] = [];

      if (product?.images) {
        media = [...media, ...product.images];
      }
      if (product?.videos) {
        media = [...media, ...product.videos];
      }

      this.auction = {
        ...rest,
        product,
        media,
      };

      this.setBreadcrumbs();
      this.setMenuOptions();
      this._cdr.detectChanges();

      const pageTitle = `${this._translateService.instant('global.auction')} ${this._translateData.transform(product?.title) || ''}`;
      this.setPageTitle(pageTitle);
    });

    effect(() => {
      this.isAuctionDetailsLoading =
        this._auctionsService.isAuctionDetailsLoading();
    });

    effect(() => {
      this._userService.userSignal();
      if (this.auction?._id) {
        this.setMenuOptions();
      }
    });
  }

  get stateLabelKey(): string {
    switch (this.auction?.state) {
      case AuctionStatesEnum.In_The_Future:
        return 'auctions.stateFuture';
      case AuctionStatesEnum.Expired:
        return 'auctions.stateExpired';
      default:
        return 'auctions.liveAuction';
    }
  }

  get statusLabelKey(): string {
    switch (this.auction?.state) {
      case AuctionStatesEnum.In_The_Future:
        return 'auctions.stateFuture';
      case AuctionStatesEnum.Expired:
        return 'auctions.stateExpired';
      default:
        return 'auctions.activeRoom';
    }
  }

  public openQrCodeDialog(barcodeContent: string): void {
    this._dialogService.open(BarcodeDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'products.qrCode',
        productTitle: this._translateData.transform(this.auction?.title),
        productCode: this.auction?.code || this.auction?.product?.code,
        barcodeContent,
      },
    });
  }

  public setMenuOptions(): void {
    const options: PreviewMenuOptionsType[] = [
      {
        id: 1,
        name: 'global.share',
        optionClasses:
          '!flex-row menu-options__item--icon-only menu-options__item--header-action',
        icon: 'share',
        onSelectOption: (barcodeContent: string) => {
          this.openQrCodeDialog(barcodeContent);
        },
      },
    ];

    if (
      this._hasAccessPipe.transform([
        Roles.CreateAuctionAll,
        Roles.CreateAuctionSelf,
      ])
    ) {
      options.push({
        id: 2,
        name: 'auctions.createNewAuction',
        optionClasses:
          '!flex-row menu-options__item--icon-only menu-options__item--header-action',
        icon: 'add',
        onSelectOption: () => {
          window.open(
            `${this.panelUrl}/auctions/create`,
            '_blank',
            'noopener,noreferrer'
          );
        },
      });
    }

    this.menuOptions = options;
  }

  public setPageTitle(content?: string): void {
    if (content) {
      this._titleService.setTitle(content);
    }
  }

  private setBreadcrumbs(): void {
    const auctionTitle =
      this._translateData.transform(this.auction?.title) ||
      this._translateService.instant('routes.auctionDetails');

    this.breadcrumbsData = [
      {
        id: 1,
        breadCrumbTitle: 'routes.home',
        breadCrumbUrl: '/',
      },
      {
        id: 2,
        breadCrumbTitle: 'header.marketplace',
        breadCrumbUrl: '/products',
      },
      {
        id: 3,
        breadCrumbTitle: 'auctions.biddingRooms',
        breadCrumbUrl: '/auctions',
      },
      {
        id: 4,
        breadCrumbTitle: auctionTitle,
      },
    ];
  }
}
